import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Col, Form, Row, Spin } from 'antd'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
  FormTextareaInput,
} from '../../../common/components/sections'
import { PERMISSIONS } from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import { idPropType } from '../../../common/prop-types'

import { useUpdateIngredientNotesMutation } from '../../hooks'
import { setIngredientPageIsDirty } from '../../redux/actions'

import { ingredientNotesFormInitialValues } from './initial-values'

const TEXTAREA_MAX_ROWS = 100

const IngredientNotesForm = ({ id, notes }) => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.INGREDIENT.UPDATE_NOTES,
  ])

  const [
    updateIngredientNotes,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateIngredientNotesMutation()

  const initialValues = useMemo(
    () => ingredientNotesFormInitialValues({ notes }),
    [notes],
  )

  const handleSaveData = useCallback(
    data => updateIngredientNotes(id, data),
    [id, updateIngredientNotes],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Ingredient notes error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [notes, resetForm])

  useEffect(() => {
    dispatch(setIngredientPageIsDirty({ isNotesDirty: isDirty }))
  }, [dispatch, isDirty])

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="ingredientNotesForm"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <Row gutter={[16]}>
        <Col span={12}>
          <FormTextareaInput
            disabled={!isFormEnabled}
            name="notes"
            label="Notes"
            labelAbove
            placeholder="Input notes..."
            rows={4}
            style={{
              resize: 'vertical',
              maxHeight: `${TEXTAREA_MAX_ROWS * 24}px`,
            }}
          />
        </Col>
      </Row>
      <FormButtons
        permissions={[PERMISSIONS.INGREDIENT.UPDATE_NOTES]}
        isDisabled={!isDirty}
        onReset={resetForm}
      />
    </DirtyPromptForm>
  )
}

IngredientNotesForm.propTypes = {
  id: idPropType.isRequired,
  notes: PropTypes.string,
}

IngredientNotesForm.defaultProps = {
  notes: undefined,
}

export default IngredientNotesForm
